import React from 'react'
import { Switch, Redirect, Route, withRouter } from 'react-router-dom'

import {
  CityManagerRoute,
  PrivateRoute,
  AdminRoute,
  ClientAdminRoute,
  ClientAndClientAdminRoute,
} from 'Config/Routing/auth'
import { ProfilePage } from './../../Containers/Profile'
import CampaignsRouter from 'Config/Routing/CampaignsRouter'
import MissionRouter from 'Config/Routing/MissionRouter'
import { CountryRouter } from 'Config/Routing/CountryRouter'
import { CityRouter } from 'Config/Routing/CityRouter'
import ClientRouter from 'Config/Routing/ClientRouter'
import SettingsRouter from 'Config/Routing/SettingsRouter'
import UserManagementRouter from './../../Config/Routing/UserManagement'
import ClientRosters from 'Containers/ClientRosters'
import ChatRouter from 'Config/Routing/ChatRouter'
import ClientCampaignsRouter from 'Config/Routing/ClientCampaignsRouter'

import { NotFound, NotAuthorised } from 'Components/Page'
import { PATHS } from 'Config/Routing/paths'
import { getNextLink } from '../../Utils/Link'
import { authorization } from '../../Module/Auth/Auth'
import CityAdd from './../../Containers/City/CityAdd'
import CityList from 'Containers/City/List'
import CountryList from 'Containers/Country'
import CountriesData from './../../Containers/City/DataHOCs/CountriesHOC'
import CountryData from './../../Containers/City/DataHOCs/CountryHOC'
import { ROLE_SPY } from '../../shared/constants'
import ChatRouterV2 from './ChatRouterV2'
import HomeRouter from './HomeRouter'
import ClientSettingsRouter from './ClientSettingsRouter'
import IntegrationRouter from './IntegrationRouter'
import DesignManagementRouter from './DesignManagementRouter'
import { StrictCityManagerRoute } from './auth'

/**
 * Check to which page user should be redirected after login
 * @returns {String}
 */
const getDefaultRoute = user => {
  const { role, client } = user
  if (role === ROLE_SPY) {
    return PATHS.dashboard.cities
  } else if (authorization.isSelfServeEnabled()) {
    return `${PATHS.dashboard.home}`
  } else if (authorization.isClientRole()) {
    return `${PATHS.dashboard.clientCampaigns}${client ? `/${client.id}` : ''}`
  }
  return PATHS.dashboard.campaigns + '?Status=Active'
}

const isNeedToHideClients = authorization.isCityManagerAssistant()

const DashboardRouter = props => {
  const user = authorization.getUser()
  const { role } = user

  return (
    <Switch>
      <Route path={props.match.url + PATHS.dashboard.countries + '/:id'}>
        <CityManagerRoute>
          <CountryData>
            <CountryRouter {...props} />
          </CountryData>
        </CityManagerRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.countries}>
        <StrictCityManagerRoute>
          <CountriesData>
            <CountryList {...props} />
          </CountriesData>
        </StrictCityManagerRoute>
      </Route>
      <Route exact path={props.match.url + PATHS.dashboard.cities}>
        <CityManagerRoute>
          <CityList {...props} />
        </CityManagerRoute>
      </Route>
      <Route
        exact
        path={props.match.url + PATHS.dashboard.cities + PATHS.cities.add}
      >
        <AdminRoute>
          <CountriesData>
            <CityAdd {...props} />
          </CountriesData>
        </AdminRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.cities + '/:id'}>
        <CityManagerRoute>
          <CityRouter {...props} />
        </CityManagerRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.clients}>
        <CityManagerRoute isNeedToHide={isNeedToHideClients}>
          <ClientRouter {...props} />
        </CityManagerRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.mission}>
        <PrivateRoute>
          <MissionRouter />
        </PrivateRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.chat}>
        <PrivateRoute>
          <ChatRouter />
        </PrivateRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.chatV2}>
        <PrivateRoute>
          <ChatRouterV2 />
        </PrivateRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.campaigns}>
        <PrivateRoute isRestricted={authorization.isSpy()}>
          <CampaignsRouter />
        </PrivateRoute>
      </Route>
      <Route
        path={props.match.url + PATHS.dashboard.clientCampaigns + '/:clientId'}
      >
        <ClientCampaignsRouter />
      </Route>
      <Route path={props.match.url + PATHS.dashboard.home}>
        <HomeRouter />
      </Route>
      <Route path={props.match.url + PATHS.dashboard.rosters}>
        <PrivateRoute>
          <ClientRosters />
        </PrivateRoute>
      </Route>
      <Route exact path={props.match.url + PATHS.dashboard.profile}>
        <PrivateRoute>
          <ProfilePage />
        </PrivateRoute>
      </Route>
      <Route exact path={props.match.url + PATHS.dashboard.notFound}>
        <PrivateRoute>
          <NotFound {...props} />
        </PrivateRoute>
      </Route>
      <Route exact path={props.match.url + PATHS.dashboard.notAuthorised}>
        <PrivateRoute>
          <NotAuthorised />
        </PrivateRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.settings}>
        <StrictCityManagerRoute>
          <SettingsRouter />
        </StrictCityManagerRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.userManagement}>
        <ClientAdminRoute>
          <UserManagementRouter />
        </ClientAdminRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.integrations}>
        <ClientAndClientAdminRoute {...props}>
          <Route
            render={({ match }) => {
              if (match.isExact) {
                const { client } = authorization.getUser()
                const path = `${PATHS.main.dashboard}${
                  client ? `/${client.id}` : ''
                }${PATHS.dashboard.integrations}`
                window.location.href = getNextLink(path)
                return null
              }
              return <IntegrationRouter />
            }}
          />
        </ClientAndClientAdminRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.clientSettings}>
        <ClientAndClientAdminRoute {...props}>
          <ClientSettingsRouter userRole={role} />
        </ClientAndClientAdminRoute>
      </Route>
      <Route path={props.match.url + PATHS.dashboard.designManagement}>
        <ClientAndClientAdminRoute {...props}>
          <DesignManagementRouter />
        </ClientAndClientAdminRoute>
      </Route>
      <Redirect
        from={props.match.url}
        to={props.match.url + getDefaultRoute(user)}
      />
    </Switch>
  )
}

export default withRouter(DashboardRouter)
